/**
 * Admin Route Guard
 * Protects against accidental admin API calls from public pages
 */

import { getAdminGuardShell } from './bus';
import { raiseSecurityAlert } from './telemetry';

const MARKETING_PATH_PATTERNS: RegExp[] = [
  /^\/$/,
  /^\/auth(?:\/.*)?$/,
  /^\/how-it-works$/,
  /^\/privacy$/,
  /^\/terms$/,
  /^\/p3-bridge-demo$/,
  /^\/user-wallet-demo$/,
  /^\/tg(?:\/?|$)/,
];

const AUTH_HANDOFF_PATH_PATTERNS: RegExp[] = [
  /^\/auth\/(?:callback|complete|confirm|handoff|hand-off|exchange|finish|return)(?:\/|$)/,
];

const AUTHENTICATED_PATH_PATTERNS: RegExp[] = [
  /^\/verify(?:\/|$)/,
  /^\/(dashboard|bridge-customer)(?:\/|$)/,
  /^\/address(?:\/|$)/,
  /^\/app\/user\//,
  /^\/checkout(?:\/|$)/,
  /^\/wallet(?:\/|$)/,
  /^\/settings(?:\/|$)/,
  /^\/admin(?:\/|$)/,
  /^\/analytics(?:\/|$)/,
  ...AUTH_HANDOFF_PATH_PATTERNS,
];

function isMarketingPath(path: string): boolean {
  return MARKETING_PATH_PATTERNS.some(pattern => pattern.test(path));
}

function isAuthenticatedPath(path: string): boolean {
  return AUTHENTICATED_PATH_PATTERNS.some(pattern => pattern.test(path));
}

const SURFACE_ALLOWLIST = new Set(['/admin', '/']);

function isAllowlistedPath(path: string): boolean {
  if (SURFACE_ALLOWLIST.has(path)) {
    return true;
  }

  for (const safe of SURFACE_ALLOWLIST) {
    if (safe !== '/' && path.startsWith(`${safe}/`)) {
      return true;
    }
  }

  if (path.startsWith('/auth')) {
    return true;
  }

  return false;
}

export function trackAdminCallOnPublicRoute(url: string) {
  // Only track in production/staging
  const isDevRuntime = import.meta.env.DEV && import.meta.env.MODE !== 'test';
  if (isDevRuntime) return;

  const currentPath = window.location.pathname;
  if (isAllowlistedPath(currentPath)) {
    return;
  }
  const shell = getAdminGuardShell();
  const isAuthenticatedShell = shell === 'authenticated';
  const isPublicPage = !isAuthenticatedShell && !isAuthenticatedPath(currentPath) && isMarketingPath(currentPath);

  // Check if this is an admin API call on a public page
  const isAdminEndpoint = url.includes('/functions/v1/admin') || url.includes('/api/admin');

  if (isAdminEndpoint && isPublicPage) {
    raiseSecurityAlert({
      type: 'admin_rpc_on_public_route',
      url,
      path: currentPath,
      timestamp: Date.now()
    });
  }

  // Track deprecated admin-rpc usage
  if (url.includes('/functions/v1/admin-rpc')) {
    console.warn('[DEPRECATED] Legacy admin-rpc endpoint used', {
      url,
      path: currentPath,
      timestamp: new Date().toISOString()
    });
  }
}

export function attachFetchGuard() {
  if (typeof window === 'undefined') return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = function(...args: Parameters<typeof fetch>) {
    const input = args[0];
    const url = typeof input === 'string' ? input : (input as Request).url;
    trackAdminCallOnPublicRoute(url);
    return originalFetch(...args);
  };
}